import { useState, useRef } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "@/hooks/use-workspace";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, withCsrfHeader } from "@/lib/queryClient";
import { Loader2, Upload, Trash2, ImageIcon, Lock, Info, Globe, LayoutDashboard, Mail, Palette, Type } from "lucide-react";
import { Input } from "@/components/ui/input";

import type { BrandingSettings } from "@shared/schema";
import { WhiteLabelCard } from "@/components/white-label-card";

// `/api/admin/branding` surfaces resolved tier entitlements:
// - allowsCustomBranding: logo, sidebar icon, signature logo (general branding)
// - allowsWhiteLabel: favicon, brand colors, typography (white-label features)
type BrandingSettingsWithEntitlement = BrandingSettings & {
  allowsCustomBranding?: boolean;
  allowsWhiteLabel?: boolean;
};

function ColorRow({ id, label, value, onChange }: { id: string; label: string; value: string | null; onChange: (v: string) => void }) {
  const safe = value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : "";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <Label htmlFor={id}>{label}</Label>
      </div>
      <Input
        id={id}
        type="color"
        value={safe || "#2563eb"}
        onChange={(e) => onChange(e.target.value)}
        className="w-14 h-9 p-1 cursor-pointer"
        data-testid={`input-${id}`}
      />
      <Input
        value={safe}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#2563eb"
        className="w-28 font-mono text-sm"
        data-testid={`input-${id}-text`}
      />
    </div>
  );
}

interface BrandingSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

type UploadType = "logo" | "favicon" | "sidebarIcon" | "signatureLogo";

export function BrandingSettingsDialog({ open, onOpenChange, inPanel = false }: BrandingSettingsDialogProps) {
  const { toast } = useToast();
  const { workspaceSlug } = useWorkspace();
  const queryClient = useQueryClient();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const sidebarIconInputRef = useRef<HTMLInputElement>(null);
  const signatureLogoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingType, setUploadingType] = useState<UploadType | null>(null);

  const { data: branding, isLoading } = useQuery<BrandingSettingsWithEntitlement>({
    queryKey: ["/api/admin/branding"],
    enabled: open,
  });
  // Default to TRUE so older API responses don't accidentally disable controls.
  // Server still enforces the gate on PATCH.
  const allowsCustomBranding = branding?.allowsCustomBranding ?? true;
  // White-label entitlement: favicon, brand colors, typography.
  const allowsWhiteLabel = branding?.allowsWhiteLabel ?? true;

  const updateAssetMutation = useMutation({
    mutationFn: async ({ type, path, fileName, contentType }: { type: UploadType; path: string; fileName: string; contentType: string }) => {
      const body = { path, fileName, contentType };
      const response = await apiRequest("POST", `/api/admin/branding/${type}`, body);
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/logo"] });
      const labels: Record<UploadType, string> = { logo: "Invoice Logo", favicon: "Favicon", sidebarIcon: "Sidebar Icon", signatureLogo: "Email Signature Logo" };
      toast({ title: `${labels[variables.type]} Updated`, description: "Your branding has been saved successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update branding.", variant: "destructive" });
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: async (type: UploadType) => {
      const response = await apiRequest("DELETE", `/api/admin/branding/${type}`);
      return response.json();
    },
    onSuccess: (_, type) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/logo"] });
      const labels: Record<UploadType, string> = { logo: "Invoice Logo", favicon: "Favicon", sidebarIcon: "Sidebar Icon", signatureLogo: "Email Signature Logo" };
      toast({ title: `${labels[type]} Deleted`, description: "Your branding asset has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete asset.", variant: "destructive" });
    },
  });

  const updateColorsMutation = useMutation({
    mutationFn: async (colors: { primaryColor?: string | null; secondaryColor?: string | null; accentColor?: string | null }) => {
      const response = await apiRequest("PATCH", "/api/admin/branding/colors", colors);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/public", workspaceSlug] });
      toast({ title: "Brand colors saved" });
    },
    onError: (error: any) => {
      toast({ title: "Save failed", description: error.message || "Could not save colors.", variant: "destructive" });
    },
  });

  const updateTypographyMutation = useMutation({
    mutationFn: async (fonts: { bodyFontFamily?: string | null; displayFontFamily?: string | null; monoFontFamily?: string | null; wordmarkText?: string | null; poweredByText?: string | null }) => {
      const response = await apiRequest("PATCH", "/api/admin/branding/typography", fonts);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding/public", workspaceSlug] });
      toast({ title: "Typography saved" });
    },
    onError: (error: any) => {
      toast({ title: "Save failed", description: error.message || "Could not save fonts.", variant: "destructive" });
    },
  });

  const toggleForceLogoMutation = useMutation({
    mutationFn: async (forceLogoInSignature: boolean) => {
      const response = await apiRequest("PATCH", "/api/admin/branding/force-logo", { forceLogoInSignature });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/branding"] });
      toast({ 
        title: "Settings Updated", 
        description: "Email signature logo settings have been saved." 
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update settings.", variant: "destructive" });
    },
  });

  // Brand name ("Email From" name) lives on the workspace and is read/written
  // through the same /api/admin/white-label endpoint the WhiteLabelCard uses
  // (gated only by the brandingSettings permission). We surface just this one
  // field here so the dashboard "Set brand name" CTA leads somewhere useful;
  // the rest of the white-label controls stay tier-gated/hidden.
  const { data: whiteLabel } = useQuery<{ customEmailFromName: string | null }>({
    queryKey: ["/api/admin/white-label"],
    enabled: open,
  });

  const saveBrandNameMutation = useMutation({
    mutationFn: async (name: string | null) => {
      const response = await apiRequest("PATCH", "/api/admin/white-label", { customEmailFromName: name });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/white-label"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspace/brand-status"] });
      toast({ title: "Brand name saved" });
    },
    onError: (error: any) => {
      toast({ title: "Save failed", description: error.message || "Could not save brand name.", variant: "destructive" });
    },
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>, type: UploadType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isFavicon = type === "favicon";
    const acceptedTypes = isFavicon 
      ? ["image/png", "image/x-icon", "image/vnd.microsoft.icon", "image/ico", "image/svg+xml"]
      : ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];

    if (!acceptedTypes.some(t => file.type.includes(t.split("/")[1])) && !file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please select a valid image file.", variant: "destructive" });
      return;
    }

    const maxSize = isFavicon ? 1 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ 
        title: "File Too Large", 
        description: `Please select an image under ${isFavicon ? "1MB" : "5MB"}.`, 
        variant: "destructive" 
      });
      return;
    }

    setUploadingType(type);

    try {
      const response = await fetch("/api/admin/branding/upload-url", {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({
          name: file.name,
          size: file.size,
          contentType: file.type,
          assetType: type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to get upload URL");
      }

      const { uploadURL, objectPath } = await response.json();

      const uploadResponse = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      await updateAssetMutation.mutateAsync({
        type,
        path: objectPath,
        fileName: file.name,
        contentType: file.type,
      });
    } catch (error: any) {
      toast({ title: "Upload Failed", description: error.message || "Failed to upload asset.", variant: "destructive" });
    } finally {
      setUploadingType(null);
      const inputRefs: Record<UploadType, React.RefObject<HTMLInputElement>> = {
        logo: logoInputRef,
        favicon: faviconInputRef,
        sidebarIcon: sidebarIconInputRef,
        signatureLogo: signatureLogoInputRef,
      };
      const inputRef = inputRefs[type];
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  // Prefer the resolved streaming URLs (served through the same
  // content-type-safe routes as the live sidebar/logo) so the preview and
  // runtime render through the identical path — an asset that renders here
  // renders there. Fall back to the raw object path for older responses.
  const logoUrl = (branding as any)?.logoImageUrl || branding?.logoPath || null;
  const faviconUrl = (branding as any)?.faviconImageUrl || branding?.faviconPath || null;
  const sidebarIconUrl = (branding as any)?.sidebarIconImageUrl || branding?.sidebarIconPath || null;
  const signatureLogoUrl = (branding as any)?.signatureLogoImageUrl || (branding as any)?.signatureLogoPath || null;

  const renderAssetUploader = (
    type: UploadType,
    label: string,
    description: string,
    url: string | null,
    fileName: string | null | undefined,
    inputRef: React.RefObject<HTMLInputElement>,
    icon: React.ReactNode,
    accept: string,
    sizeHint: string
  ) => (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {icon}
        {label}
      </Label>
      <Card className="border-dashed">
        <CardContent className="p-4">
          {url ? (
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-muted rounded-lg overflow-hidden">
                <img
                  src={url}
                  alt={`Current ${label.toLowerCase()}`}
                  className="max-h-14 max-w-14 object-contain"
                  data-testid={`img-current-${type}`}
                  onError={() =>
                    console.warn(
                      `[BrandingSettings] Failed to load ${type} preview from`,
                      url,
                      "— the asset may be streaming with a wrong/missing Content-Type."
                    )
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{fileName || `${label} uploaded`}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploadingType === type}
                    data-testid={`button-replace-${type}`}
                  >
                    {uploadingType === type ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    Replace
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteAssetMutation.mutate(type)}
                    disabled={deleteAssetMutation.isPending}
                    data-testid={`button-delete-${type}`}
                  >
                    {deleteAssetMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-muted rounded-lg">
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">{description}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploadingType === type}
                  data-testid={`button-upload-${type}`}
                >
                  {uploadingType === type ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">{sizeHint}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFileSelect(e, type)}
        data-testid={`input-${type}-file`}
      />
    </div>
  );

  const innerContent = (
    <>
      <DialogHeader>
        <DialogTitle>Custom Branding</DialogTitle>
        <DialogDescription>
          Manage workspace assets and white-label appearance.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div data-testid="tabs-branding">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="brand-name" className="text-base font-medium flex items-center gap-2">
                  <Type className="w-4 h-4 text-muted-foreground" />
                  Brand name
                </Label>
                <p className="text-sm text-muted-foreground">
                  The customer-facing name that signs your emails, invoices, and
                  the public booking page. Leave blank to use your workspace name.
                </p>
                <Input
                  key={whiteLabel?.customEmailFromName ?? ""}
                  id="brand-name"
                  defaultValue={whiteLabel?.customEmailFromName ?? ""}
                  placeholder="Your brand name"
                  maxLength={255}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    const current = whiteLabel?.customEmailFromName ?? "";
                    if (next !== current) {
                      saveBrandNameMutation.mutate(next || null);
                    }
                  }}
                  data-testid="input-brand-name"
                />
              </div>

              {renderAssetUploader(
                "logo",
                "Invoice Logo",
                "Upload a logo for invoice headers",
                logoUrl,
                branding?.logoFileName,
                logoInputRef,
                <ImageIcon className="w-4 h-4" />,
                "image/*",
                "Recommended: PNG or JPG, max 5MB. Appears on invoice headers."
              )}

              {renderAssetUploader(
                "sidebarIcon",
                "Sidebar Icon",
                "Upload an icon for the sidebar header",
                sidebarIconUrl,
                branding?.sidebarIconFileName,
                sidebarIconInputRef,
                <LayoutDashboard className="w-4 h-4" />,
                "image/*",
                "Recommended: Square PNG or SVG, max 5MB. Appears next to company name."
              )}

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-base font-medium">Email Signature Settings</Label>
                </div>

                {renderAssetUploader(
                  "signatureLogo",
                  "Email Signature Logo",
                  "Upload a logo for email signatures (optional, uses invoice logo if not set)",
                  signatureLogoUrl,
                  (branding as any)?.signatureLogoFileName,
                  signatureLogoInputRef,
                  <Mail className="w-4 h-4" />,
                  "image/*",
                  "Recommended: PNG or JPG, max 5MB. Appears in email signatures. Falls back to Invoice Logo if not set."
                )}

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="force-logo-switch" className="font-medium">
                          Force Logo in Email Signatures
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          When enabled, all users must include the company logo in their email signatures
                        </p>
                      </div>
                      <Switch
                        id="force-logo-switch"
                        checked={branding?.forceLogoInSignature ?? false}
                        onCheckedChange={(checked) => toggleForceLogoMutation.mutate(checked)}
                        disabled={toggleForceLogoMutation.isPending || (!logoUrl && !signatureLogoUrl)}
                        data-testid="switch-force-logo"
                      />
                    </div>
                    {!logoUrl && !signatureLogoUrl && (
                      <div className="flex items-center gap-2 mt-3 p-2 bg-muted rounded-md">
                        <Info className="w-4 h-4 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">
                          Upload a logo first to enable this setting
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="powered-by-text" className="text-base font-medium flex items-center gap-2">
                  <Type className="w-4 h-4 text-muted-foreground" />
                  "Powered by" Caption
                </Label>
                <p className="text-sm text-muted-foreground">
                  Customize the attribution text shown on public pages and client-facing surfaces.
                </p>
                <Input
                  id="powered-by-text"
                  defaultValue={branding?.poweredByText ?? ""}
                  placeholder="Powered by Clarik.io"
                  maxLength={120}
                  onBlur={(e) =>
                    updateTypographyMutation.mutate({
                      poweredByText: e.target.value.trim() || null,
                    })
                  }
                  data-testid="input-powered-by-text"
                />
              </div>
            </div>
            {false && (
              <div>

              <fieldset disabled={!allowsWhiteLabel} className="space-y-6 disabled:opacity-60 disabled:pointer-events-none">
                {renderAssetUploader(
                  "favicon",
                  "Website Favicon",
                  "Upload a favicon for browser tabs",
                  faviconUrl,
                  branding?.faviconFileName,
                  faviconInputRef,
                  <Globe className="w-4 h-4" />,
                  ".ico,.png,.svg,image/x-icon,image/png,image/svg+xml",
                  "Recommended: 32x32 or 64x64 ICO/PNG, max 1MB. Appears in browser tabs."
                )}

                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      <Label className="font-medium">Brand Colors</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Used across public-facing pages: invoices, client portal,
                      booking forms, e-signature, and the payment portal.
                    </p>
                    <ColorRow
                      id="primary-color"
                      label="Primary"
                      value={branding?.primaryColor ?? null}
                      onChange={(v) => updateColorsMutation.mutate({ primaryColor: v })}
                    />
                    <ColorRow
                      id="secondary-color"
                      label="Secondary"
                      value={branding?.secondaryColor ?? null}
                      onChange={(v) => updateColorsMutation.mutate({ secondaryColor: v })}
                    />
                    <ColorRow
                      id="accent-color"
                      label="Accent"
                      value={branding?.accentColor ?? null}
                      onChange={(v) => updateColorsMutation.mutate({ accentColor: v })}
                    />
                    {(branding?.primaryColor || branding?.secondaryColor || branding?.accentColor) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateColorsMutation.mutate({ primaryColor: null, secondaryColor: null, accentColor: null })}
                        disabled={updateColorsMutation.isPending}
                        data-testid="button-reset-colors"
                      >
                        Reset to platform defaults
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      <Label className="font-medium">Typography & Wordmark</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Paste any CSS <code>font-family</code> stack
                      (e.g. <code>"Inter", system-ui, sans-serif</code>).
                      Leave blank to inherit the platform defaults.
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="body-font">Body font</Label>
                        <Input
                          id="body-font"
                          defaultValue={branding?.bodyFontFamily ?? ""}
                          placeholder='"Inter", system-ui, sans-serif'
                          onBlur={(e) =>
                            updateTypographyMutation.mutate({
                              bodyFontFamily: e.target.value.trim() || null,
                            })
                          }
                          data-testid="input-body-font"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="display-font">Display font</Label>
                        <Input
                          id="display-font"
                          defaultValue={branding?.displayFontFamily ?? ""}
                          placeholder='"Fraunces", Georgia, serif'
                          onBlur={(e) =>
                            updateTypographyMutation.mutate({
                              displayFontFamily: e.target.value.trim() || null,
                            })
                          }
                          data-testid="input-display-font"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="mono-font">Monospace font</Label>
                        <Input
                          id="mono-font"
                          defaultValue={branding?.monoFontFamily ?? ""}
                          placeholder='"JetBrains Mono", ui-monospace, monospace'
                          onBlur={(e) =>
                            updateTypographyMutation.mutate({
                              monoFontFamily: e.target.value.trim() || null,
                            })
                          }
                          data-testid="input-mono-font"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="wordmark-text">Wordmark text</Label>
                        <Input
                          id="wordmark-text"
                          defaultValue={branding?.wordmarkText ?? ""}
                          placeholder="clarik"
                          maxLength={120}
                          onBlur={(e) =>
                            updateTypographyMutation.mutate({
                              wordmarkText: e.target.value.trim() || null,
                            })
                          }
                          data-testid="input-wordmark-text"
                        />
                      </div>
                    </div>
                    {(branding?.bodyFontFamily || branding?.displayFontFamily || branding?.monoFontFamily || branding?.wordmarkText) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          updateTypographyMutation.mutate({
                            bodyFontFamily: null,
                            displayFontFamily: null,
                            monoFontFamily: null,
                            wordmarkText: null,
                          })
                        }
                        disabled={updateTypographyMutation.isPending}
                        data-testid="button-reset-typography"
                      >
                        Reset to platform defaults
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <WhiteLabelCard />
              </fieldset>
            </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px] max-h-[90vh] overflow-y-auto">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
